import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { PagingControls } from '../../../components/ui/PagingControls/PagingControls';
import { LoadingContainer } from '../../../components/ui/LoadingContainer/LoadingContainer';
import { SortableHeader } from '../../../components/ui/SortableHeader/SortableHeader';
import styles from './ClubMemberLookup.module.css';
import useClubMemberLookup from './useClubMemberLookup';
import useMemberStatusLookup from './useMemberStatusLookup';
import useMembershipTypeLookup from './useMembershipTypeLookup';
import {
  IFlattenedMemberDisplay,
  IClubMemberApiSearchRequest
} from '../../../utils/constants/interfaces';
import {
  ClubMemberLookupDisplayMode,
  MemberTitle,
  MemberSuffix
} from '../../../utils/constants/enums';
import Container from '../../ui/Container/Container';
import { Accordion, Row, Col, Form, Button, InputGroup } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSearch,
  faFilter,
  faTimes,
  faChevronDown,
  faChevronRight,
  faFileExport
} from '@fortawesome/free-solid-svg-icons';
import { Helpers } from '../../../utils/helpers';
import { DateInput } from '../../ui/DateInput/DateInput';

// Interface for UI state to remember filter/accordion expansion
export interface IClubMemberLookupUiState {
  showFilters: boolean;
  showAdvancedOptions: boolean;
}

// Interface for location state when returning from the editor
interface ClubMemberLookupLocationState {
  searchParams?: IClubMemberApiSearchRequest;
  uiState?: IClubMemberLookupUiState;
}

// Helper functions to convert enum values to strings
const getTitleString = (title: MemberTitle | null): string => {
  if (title === null) return '';
  switch (title) {
    case MemberTitle.None:
      return '';
    case MemberTitle.Mr:
      return 'Mr.';
    case MemberTitle.Mrs:
      return 'Mrs.';
    case MemberTitle.Ms:
      return 'Ms.';
    default:
      return '';
  }
};

const getSuffixString = (suffix: MemberSuffix | null): string => {
  if (suffix === null) return '';
  switch (suffix) {
    case MemberSuffix.None:
      return '';
    case MemberSuffix.I:
      return 'I';
    case MemberSuffix.II:
      return 'II';
    case MemberSuffix.III:
      return 'III';
    case MemberSuffix.IV:
      return 'IV';
    case MemberSuffix.MD:
      return 'MD';
    case MemberSuffix.Jr:
      return 'Jr.';
    case MemberSuffix.Sr:
      return 'Sr.';
    default:
      return '';
  }
};

// Export utility function for club members
const exportClubMembersToCsv = (data: IFlattenedMemberDisplay[]) => {
  // Define CSV columns with intelligent headers and formatters
  const columns: Array<{
    key: string;
    header: string;
    formatter?: (value: unknown, row: IFlattenedMemberDisplay) => string;
  }> = [
    { key: 'clubMember.primaryMember.legacyMemberId', header: 'Member ID' },
    { key: 'member.lastName', header: 'Last Name' },
    { key: 'member.firstName', header: 'First Name' },
    { key: 'member.middleName', header: 'Middle Name' },
    {
      key: 'member.suffix',
      header: 'Suffix',
      formatter: (value) => getSuffixString(value as MemberSuffix | null)
    },
    {
      key: 'member.title',
      header: 'Title',
      formatter: (value) => getTitleString(value as MemberTitle | null)
    },
    {
      key: 'clubMember.membershipType.name',
      header: 'Member Type',
      formatter: (value) => String(value || '')
    },
    {
      key: 'member.memberStatus.description',
      header: 'Member Status',
      formatter: (value) => String(value || '')
    },
    { key: 'member.emailAddress', header: 'Email Address' },
    // Address fields
    { key: 'member.address', header: 'Address' },
    { key: 'member.address2', header: 'Address 2' },
    { key: 'member.city', header: 'City' },
    { key: 'member.state', header: 'State' },
    { key: 'member.zipCode', header: 'ZIP' },
    // Contact fields
    { key: 'member.homePhone', header: 'Phone' },
    { key: 'member.cellPhone', header: 'Cell Phone' },
    // Date fields
    { key: 'member.dateOfBirthUtc', header: 'Birth Date' },
    { key: 'member.firstJoinedDateUtc', header: 'First Joined Date' },
    { key: 'member.joinedDateUtc', header: 'Joined Date' },
    // Other fields
    { key: 'member.profession', header: 'Profession' },
    { key: 'member.isNraMember', header: 'NRA Member' },
    { key: 'member.nraNumber', header: 'NRA Number' },
    // Status dates
    { key: 'member.regularMemberDateUtc', header: 'Regular Status Date' },
    { key: 'member.seniorMemberDateUtc', header: 'Senior Status Date' },
    { key: 'member.lifeMemberDateUtc', header: 'Life Status Date' },
    { key: 'member.honoraryMemberDateUtc', header: 'Honorary Status Date' },
    // Audit fields
    { key: 'member.createdBy', header: 'Created By' },
    { key: 'member.dateCreatedUtc', header: 'Created Date' },
    { key: 'member.lastModifiedBy', header: 'Updated By' },
    { key: 'member.lastModifiedDateUtc', header: 'Updated Date' },
    // Other fields
    { key: 'clubMember.notes', header: 'Notes' },
    // Family member fields (secondary member)
    {
      key: 'clubMember.secondaryMember.legacyMemberId',
      header: 'Family Member ID',
      formatter: (value, row) => (row.isSecondary ? String(value || '') : '')
    },
    {
      key: 'clubMember.secondaryMember.relationship',
      header: 'Relationship',
      formatter: (value, row) => (row.isSecondary ? String(value || '') : '')
    }
  ];

  // Create CSV headers
  const headers = columns.map((col) => col.header);

  // Create CSV rows
  const csvRows = data.map((row) => {
    return columns.map((col) => {
      let value = getNestedValue(row, col.key);

      // Use formatter if available
      if (col.formatter) {
        value = col.formatter(value, row);
      }

      // Handle null/undefined values
      if (value === null || value === undefined) {
        return '';
      }

      // Format dates
      if (value instanceof Date) {
        return value.toLocaleDateString();
      }

      // Format boolean values
      if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
      }

      // Escape quotes and wrap in quotes if contains comma, quote, or newline
      const stringValue = String(value);
      if (stringValue.includes(',') || stringValue.includes('"') || stringValue.includes('\n')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }

      return stringValue;
    });
  });

  // Combine headers and rows
  const csvContent = [headers, ...csvRows].map((row) => row.join(',')).join('\n');

  // Create and download file
  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', 'club_members.csv');
  link.style.visibility = 'hidden';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Utility function to get nested object values
const getNestedValue = (obj: unknown, path: string): unknown => {
  return path.split('.').reduce((current: unknown, key: string) => {
    if (current && typeof current === 'object' && current !== null && key in current) {
      return (current as Record<string, unknown>)[key];
    }
    return null;
  }, obj);
};

interface ClubMemberLookupProps {
  clubMemberId?: string;
  visible?: boolean;
  modalView?: boolean;
  displayMode?: ClubMemberLookupDisplayMode;
  showInactiveFilter?: boolean;
  requireKeywords?: boolean;
  onChange: (
    clubMemberId: string,
    searchParams: IClubMemberApiSearchRequest,
    uiState: IClubMemberLookupUiState
  ) => void;
}

export const ClubMemberLookup = ({
  clubMemberId = '',
  visible = true,
  modalView = false,
  displayMode = ClubMemberLookupDisplayMode.DEFAULT,
  requireKeywords = false,
  onChange
}: ClubMemberLookupProps) => {
  const location = useLocation();
  const locationState = location.state as ClubMemberLookupLocationState | null;

  // Get initial search params and UI state from location state (when returning from editor)
  const initialSearchParams = locationState?.searchParams || null;
  const initialUiState = locationState?.uiState || null;

  const statusLookup = useMemberStatusLookup();
  const membershipTypeLookup = useMembershipTypeLookup();
  const [showFilters, setShowFilters] = useState(initialUiState?.showFilters ?? false);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(
    initialUiState?.showAdvancedOptions ?? false
  );
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [defaultStatusInitialized, setDefaultStatusInitialized] = useState(false);

  // Callback to get current UI state for the hook
  const getUiState = (): IClubMemberLookupUiState => ({
    showFilters,
    showAdvancedOptions
  });

  const manager = useClubMemberLookup(
    clubMemberId,
    requireKeywords,
    onChange,
    initialSearchParams,
    initialUiState,
    getUiState
  );

  // Initialize default status to "Active" when status options are loaded
  // Skip if we have initial search params (restoring from navigation)
  useEffect(() => {
    if (!defaultStatusInitialized && statusLookup.statusOptions.length > 0) {
      const activeStatus = statusLookup.statusOptions.find((s) => s.description === 'Active');
      // Only set default if no status is set AND we don't have initial search params
      if (activeStatus && manager.searchParameters.status.length === 0 && !initialSearchParams) {
        manager.updateSearchField('status', [activeStatus.id]);
      }
      setDefaultStatusInitialized(true);
    }
  }, [statusLookup.statusOptions, defaultStatusInitialized, manager, initialSearchParams]);

  const handleCheckboxChange = (
    field: 'includeSecondaryAdults' | 'nraMembersOnly',
    checked: boolean
  ) => {
    manager.updateSearchField(field, checked);
  };

  const handleMainStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    if (value === '') {
      manager.updateSearchField('status', []);
    } else {
      const statusId = parseInt(value);
      manager.updateSearchField('status', [statusId]);
    }
  };

  const handleStatusChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const values = Array.from(e.target.selectedOptions, (option) => parseInt(option.value));
    manager.updateSearchField('status', values);
  };

  const handleTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const values = Array.from(e.target.selectedOptions, (option) => parseInt(option.value));
    manager.updateSearchField('membershipTypes', values);
  };

  // Clear all filters
  const handleClearFilters = () => {
    if (manager.resetSearch) {
      manager.resetSearch();
    }
  };

  // Handle search button click
  const handleSearch = () => {
    void manager.search();
  };

  // Handle export button click
  const handleExport = () => {
    exportClubMembersToCsv(manager.flattenedResults);
  };

  // Handle Enter key press to trigger search
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearch();
    }
  };

  // Toggle row expansion
  const toggleRowExpansion = (memberId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent row click from triggering
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(memberId)) {
        newSet.delete(memberId);
      } else {
        newSet.add(memberId);
      }
      return newSet;
    });
  };

  if (!visible) return <></>;

  return (
    <Container visible={true} className={styles.lookupSearchContainer}>
      <div className={styles.filterSection}>
        {/* Main Search Bar */}
        <Row className="mb-3">
          <Col>
            <InputGroup>
              <InputGroup.Text>
                <FontAwesomeIcon icon={faSearch} />
              </InputGroup.Text>
              <Form.Control
                placeholder="Quick Search"
                value={manager.searchParameters.quickSearch}
                onChange={(e) => manager.updateSearchField('quickSearch', e.target.value)}
                onKeyDown={handleKeyDown}
              />
              <Form.Select
                value={
                  manager.searchParameters.status.length > 0
                    ? manager.searchParameters.status[0]
                    : statusLookup.statusOptions.find((s) => s.description === 'Active')?.id || ''
                }
                onChange={handleMainStatusChange}
                style={{ maxWidth: '200px' }}>
                {statusLookup.statusOptions.map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.description}
                  </option>
                ))}
              </Form.Select>
              <Button variant="primary" onClick={handleSearch}>
                Search
              </Button>
              <Button
                variant="outline-secondary"
                onClick={() => setShowFilters(!showFilters)}
                className={showFilters ? 'active' : ''}>
                <FontAwesomeIcon icon={faFilter} />
              </Button>
              <Button variant="outline-secondary" onClick={handleClearFilters}>
                <FontAwesomeIcon icon={faTimes} />
              </Button>
            </InputGroup>
          </Col>
        </Row>

        {/* Collapsible Filters */}
        <div className={`${styles.filtersCollapsible} ${showFilters ? styles.filtersVisible : ''}`}>
          <Row className="g-2 mb-3">
            <Col md={3}>
              <Form.Group className={styles.compactFormGroup}>
                <Form.Label className={styles.smallLabel}>ID</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={manager.searchParameters.legacyMemberId || ''}
                  onChange={(e) => manager.updateSearchField('legacyMemberId', e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className={styles.compactFormGroup}>
                <Form.Label className={styles.smallLabel}>Last Name</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={manager.searchParameters.lastName}
                  onChange={(e) => manager.updateSearchField('lastName', e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className={styles.compactFormGroup}>
                <Form.Label className={styles.smallLabel}>First Name</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={manager.searchParameters.firstName}
                  onChange={(e) => manager.updateSearchField('firstName', e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </Form.Group>
            </Col>
            <Col md={3}>
              <Form.Group className={styles.compactFormGroup}>
                <Form.Label className={styles.smallLabel}>Email</Form.Label>
                <Form.Control
                  size="sm"
                  type="text"
                  value={manager.searchParameters.emailAddress}
                  onChange={(e) => manager.updateSearchField('emailAddress', e.target.value)}
                  onKeyDown={handleKeyDown}
                />
              </Form.Group>
            </Col>
          </Row>

          <Row className="g-2 mb-3">
            <Col md={3}>
              <DateInput
                label="Birth Date"
                size="sm"
                value={manager.searchParameters.birthDate}
                onChange={(date) => manager.updateSearchField('birthDate', date)}
              />
            </Col>
            <Col md={3}>
              <DateInput
                label="Joined (from)"
                size="sm"
                value={manager.searchParameters.joinedDateFrom}
                onChange={(date) => manager.updateSearchField('joinedDateFrom', date)}
              />
            </Col>
            <Col md={3}>
              <DateInput
                label="Joined (thru)"
                size="sm"
                value={manager.searchParameters.joinedDateThru}
                onChange={(date) => manager.updateSearchField('joinedDateThru', date)}
              />
            </Col>
            <Col md={3} className="d-flex align-items-end">
              <Form.Check
                type="checkbox"
                id="includeSecondaryAdults"
                label="Include Secondary Adults"
                checked={manager.searchParameters.includeSecondaryAdults}
                onChange={(e) => handleCheckboxChange('includeSecondaryAdults', e.target.checked)}
                className={styles.compactCheck}
              />
            </Col>
          </Row>

          {/* Advanced Search Options */}
          <Accordion
            className="mb-3"
            activeKey={showAdvancedOptions ? '0' : undefined}
            onSelect={(eventKey) => setShowAdvancedOptions(eventKey === '0')}>
            <Accordion.Item eventKey="0">
              <Accordion.Header>Additional Search Options</Accordion.Header>
              <Accordion.Body className="p-3">
                <Row className="g-2 mb-3">
                  <Col md={3}>
                    <DateInput
                      label="First Joined (from)"
                      size="sm"
                      value={manager.searchParameters.firstJoinedFrom}
                      onChange={(date) => manager.updateSearchField('firstJoinedFrom', date)}
                    />
                  </Col>
                  <Col md={3}>
                    <DateInput
                      label="First Joined (thru)"
                      size="sm"
                      value={manager.searchParameters.firstJoinedThru}
                      onChange={(date) => manager.updateSearchField('firstJoinedThru', date)}
                    />
                  </Col>
                  <Col md={3}>
                    <Form.Group className={styles.compactFormGroup}>
                      <Form.Label className={styles.smallLabel}>Profession</Form.Label>
                      <Form.Control
                        size="sm"
                        type="text"
                        value={manager.searchParameters.profession}
                        onChange={(e) => manager.updateSearchField('profession', e.target.value)}
                        onKeyDown={handleKeyDown}
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3} className="d-flex align-items-end">
                    <Form.Check
                      type="checkbox"
                      id="nraMembersOnly"
                      label="NRA Members Only"
                      checked={manager.searchParameters.nraMembersOnly}
                      onChange={(e) => handleCheckboxChange('nraMembersOnly', e.target.checked)}
                      className={styles.compactCheck}
                    />
                  </Col>
                </Row>

                <Row className="g-2">
                  <Col md={6}>
                    <Form.Group className={styles.compactFormGroup}>
                      <Form.Label className={styles.smallLabel}>Status</Form.Label>
                      <Form.Select
                        size="sm"
                        multiple
                        value={manager.searchParameters.status.map((s) => s.toString())}
                        onChange={handleStatusChange}
                        className={styles.multiSelect}>
                        {statusLookup.statusOptions.map((status) => (
                          <option key={status.id} value={status.id}>
                            {status.description}
                          </option>
                        ))}
                      </Form.Select>
                      <small className={styles.helperText}>Hold Ctrl/Cmd to select multiple</small>
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className={styles.compactFormGroup}>
                      <Form.Label className={styles.smallLabel}>Type</Form.Label>
                      <Form.Select
                        size="sm"
                        multiple
                        value={manager.searchParameters.membershipTypes.map((mt) => mt.toString())}
                        onChange={handleTypeChange}
                        className={styles.multiSelect}
                        disabled={membershipTypeLookup.loading}>
                        {membershipTypeLookup.membershipTypes.map((type) => (
                          <option key={type.id} value={type.id}>
                            {type.name}
                          </option>
                        ))}
                      </Form.Select>
                      <small className={styles.helperText}>Hold Ctrl/Cmd to select multiple</small>
                    </Form.Group>
                  </Col>
                </Row>
              </Accordion.Body>
            </Accordion.Item>
          </Accordion>
        </div>

        {/* Search Results */}
        <div className={styles.lookupSearchResultContainer}>
          <LoadingContainer isLoading={manager.loading}>
            {/* No search performed yet */}
            <Container
              visible={
                !manager.hasSearchParameters && manager.clubMemberPagedResults.resultCount === 0
              }>
              <div className={styles.initialState}>
                <FontAwesomeIcon icon={faSearch} className={styles.initialStateIcon} />
                <div className={styles.initialStateText}>Search for club members</div>
                <div className={styles.initialStateSubtext}>
                  Use the search bar above or filters to find club members
                </div>
              </div>
            </Container>

            {/* Search performed but no results */}
            <Container
              visible={
                manager.hasSearchParameters && manager.clubMemberPagedResults.resultCount === 0
              }>
              <div className={styles.noResults}>
                <FontAwesomeIcon icon={faSearch} className={styles.noResultsIcon} />
                <div className={styles.noResultsText}>No members found</div>
                <div className={styles.noResultsSubtext}>
                  No members found matching your search criteria. Try adjusting your search terms or
                  filters.
                </div>
              </div>
            </Container>

            <Container visible={manager.clubMemberPagedResults.resultCount > 0}>
              <>
                <Container visible={displayMode === ClubMemberLookupDisplayMode.DEFAULT}>
                  <div
                    className={
                      modalView ? styles.lookupSearchResultsModal : styles.lookupSearchResults
                    }>
                    <table className="table table-hover table-sm">
                      <thead>
                        <tr>
                          <th style={{ width: '40px' }}></th>
                          <SortableHeader
                            text="Last Name"
                            sortField="lastname"
                            active={manager.sortField === 'lastname'}
                            sortDirection={manager.sortDirection}
                            onClick={manager.changeSort}
                          />
                          <SortableHeader
                            text="First Name"
                            sortField="firstname"
                            active={manager.sortField === 'firstname'}
                            sortDirection={manager.sortDirection}
                            onClick={manager.changeSort}
                          />
                          <SortableHeader
                            text="Middle Name"
                            sortField="middlename"
                            active={manager.sortField === 'middlename'}
                            sortDirection={manager.sortDirection}
                            onClick={manager.changeSort}
                          />
                          <SortableHeader
                            text="Email"
                            sortField="email"
                            active={manager.sortField === 'email'}
                            sortDirection={manager.sortDirection}
                            onClick={manager.changeSort}
                          />
                          <SortableHeader
                            text="DOB"
                            sortField="dob"
                            active={manager.sortField === 'dob'}
                            sortDirection={manager.sortDirection}
                            onClick={manager.changeSort}
                          />
                        </tr>
                      </thead>
                      <tbody>
                        {manager.clubMemberPagedResults.results?.map(
                          (flattenedMember: IFlattenedMemberDisplay) => (
                            <>
                              <tr
                                key={`${flattenedMember.clubMemberId}-${flattenedMember.isSecondary ? 'secondary' : 'primary'}`}
                                onClick={() =>
                                  manager.selectClubMember(flattenedMember.clubMemberId.toString())
                                }
                                className={`${styles.clickableRow} ${flattenedMember.isSecondary ? styles.secondaryMemberRow : ''}`}>
                                <td
                                  className="text-center align-middle"
                                  style={{ cursor: 'pointer', userSelect: 'none' }}
                                  onClick={(e) =>
                                    toggleRowExpansion(
                                      `${flattenedMember.clubMemberId}-${flattenedMember.isSecondary ? 'secondary' : 'primary'}`,
                                      e
                                    )
                                  }>
                                  <FontAwesomeIcon
                                    icon={
                                      expandedRows.has(
                                        `${flattenedMember.clubMemberId}-${flattenedMember.isSecondary ? 'secondary' : 'primary'}`
                                      )
                                        ? faChevronDown
                                        : faChevronRight
                                    }
                                    size="sm"
                                  />
                                </td>
                                <td>{flattenedMember.member.lastName}</td>
                                <td>{flattenedMember.member.firstName}</td>
                                <td>{flattenedMember.member.middleName || '-'}</td>
                                <td>{flattenedMember.member.emailAddress}</td>
                                <td>
                                  {flattenedMember.member.dateOfBirthUtc
                                    ? new Date(
                                        flattenedMember.member.dateOfBirthUtc
                                      ).toLocaleDateString()
                                    : '-'}
                                </td>
                              </tr>
                              {expandedRows.has(
                                `${flattenedMember.clubMemberId}-${flattenedMember.isSecondary ? 'secondary' : 'primary'}`
                              ) && (
                                <tr>
                                  <td colSpan={6} className="p-0">
                                    <div className="bg-light p-3 border-top">
                                      <Row>
                                        <Col md={6}>
                                          <div className="mb-2">
                                            <strong>Member Type:</strong>{' '}
                                            {flattenedMember.isSecondary ? 'Secondary' : 'Primary'}
                                          </div>
                                          <div className="mb-2">
                                            <strong>First Name:</strong>{' '}
                                            {flattenedMember.member.firstName}
                                          </div>
                                          <div className="mb-2">
                                            <strong>Last Name:</strong>{' '}
                                            {flattenedMember.member.lastName}
                                          </div>
                                          <div className="mb-2">
                                            <strong>Date of Birth:</strong>{' '}
                                            {Helpers.toShortDate(
                                              flattenedMember.member.dateOfBirthUtc
                                            )}
                                          </div>
                                          <div className="mb-2">
                                            <strong>Email Address:</strong>{' '}
                                            {flattenedMember.member.emailAddress}
                                          </div>
                                          <div className="mt-3">
                                            <div className="border p-2 bg-white rounded">
                                              <strong>Notes:</strong>
                                              <div className="mt-1">
                                                {flattenedMember.clubMember.notes || ''}
                                              </div>
                                            </div>
                                          </div>
                                        </Col>
                                        <Col md={6}>
                                          <div className="mb-2">
                                            <strong>Address:</strong>{' '}
                                            {flattenedMember.clubMember.primaryMember.address}
                                          </div>
                                          <div className="mb-2">
                                            <strong>Address 2:</strong>{' '}
                                            {flattenedMember.clubMember.primaryMember.address2 ||
                                              '-'}
                                          </div>
                                          <div className="mb-2">
                                            <strong>City/State/Zip:</strong>{' '}
                                            {flattenedMember.clubMember.primaryMember.city},{' '}
                                            {flattenedMember.clubMember.primaryMember.state}{' '}
                                            {flattenedMember.clubMember.primaryMember.zipCode}
                                          </div>
                                          <div className="mb-2">
                                            <strong>Home Phone:</strong>{' '}
                                            {flattenedMember.clubMember.primaryMember.homePhone ||
                                              '-'}
                                          </div>
                                          <div className="mb-2">
                                            <strong>Cell Phone:</strong>{' '}
                                            {flattenedMember.clubMember.primaryMember.cellPhone ||
                                              '-'}
                                          </div>
                                          <div className="mb-2">
                                            <strong>Joined Date:</strong>{' '}
                                            {Helpers.toShortDate(
                                              flattenedMember.clubMember.primaryMember.joinedDateUtc
                                            )}
                                          </div>
                                          <div className="mb-2">
                                            <strong>Status:</strong>{' '}
                                            {flattenedMember.member.memberStatus?.description ||
                                              '-'}
                                          </div>
                                          <div className="mb-2">
                                            <strong>Membership Type:</strong>{' '}
                                            {flattenedMember.clubMember.membershipType?.name || '-'}
                                          </div>
                                        </Col>
                                      </Row>
                                    </div>
                                  </td>
                                </tr>
                              )}
                            </>
                          )
                        )}
                      </tbody>
                    </table>
                  </div>
                </Container>
                {/* SIMPLE display mode */}
                <Container
                  visible={displayMode === ClubMemberLookupDisplayMode.SIMPLE}
                  className={
                    modalView ? styles.lookupSearchResultsModal : styles.lookupSearchResults
                  }>
                  {manager.clubMemberPagedResults.results?.map(
                    (flattenedMember: IFlattenedMemberDisplay) => (
                      <div
                        key={`${flattenedMember.clubMemberId}-${flattenedMember.isSecondary ? 'secondary' : 'primary'}`}
                        className={`${styles.lookupSearchResultSimple} ${styles.clickableRow} ${flattenedMember.isSecondary ? styles.secondaryMemberRow : ''}`}
                        onClick={() =>
                          manager.selectClubMember(flattenedMember.clubMemberId.toString())
                        }>
                        <div>
                          <div>
                            {flattenedMember.member.lastName}, {flattenedMember.member.firstName}
                            {flattenedMember.isSecondary && (
                              <small className="text-muted ms-2">(Secondary)</small>
                            )}
                          </div>
                          <div className={styles.userDetails}>
                            {flattenedMember.member.emailAddress}
                          </div>
                        </div>
                      </div>
                    )
                  )}
                </Container>
                <div className="pt-3 mt-2 d-flex justify-content-center">
                  <Button
                    variant="outline-primary"
                    size="sm"
                    onClick={handleExport}
                    disabled={manager.clubMemberPagedResults.resultCount === 0}>
                    <FontAwesomeIcon icon={faFileExport} className="me-1" />
                    Export CSV
                  </Button>
                </div>
                <div className="pt-3 mt-2">
                  <PagingControls
                    page={manager.clubMemberPagedResults.currentPage ?? 1}
                    pageCount={manager.clubMemberPagedResults.pageCount ?? 1}
                    maxPlusOrMinusPagesToShow={3}
                    onPageChange={manager.changePage}
                  />
                </div>
              </>
            </Container>
          </LoadingContainer>
        </div>
      </div>
    </Container>
  );
};
